/* Curve helpers for authoring clips: easings, keyframed curves and loop phases. */

export const TAU = Math.PI * 2;

export const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);
export const lerp = (a: number, b: number, k: number): number => a + (b - a) * k;

export type Ease = (k: number) => number;
export const linear: Ease = (k) => k;
export const inQuad: Ease = (k) => k * k;
export const outQuad: Ease = (k) => 1 - (1 - k) * (1 - k);
export const inCubic: Ease = (k) => k * k * k;
export const outCubic: Ease = (k) => 1 - (1 - k) ** 3;
export const inOut: Ease = (k) => (k < 0.5 ? 4 * k * k * k : 1 - (-2 * k + 2) ** 3 / 2);
export const smooth: Ease = (k) => k * k * (3 - 2 * k);
export const inExpo: Ease = (k) => (k <= 0 ? 0 : 2 ** (10 * k - 10));
export const outExpo: Ease = (k) => (k >= 1 ? 1 : 1 - 2 ** (-10 * k));
/** Overshoots and settles (a snap that lands). */
export const outBack: Ease = (k) => {
  const c = 1.70158;
  return 1 + (c + 1) * (k - 1) ** 3 + c * (k - 1) ** 2;
};
/** A damped wobble that settles at 1. */
export const outElastic: Ease = (k) => (k <= 0 ? 0 : k >= 1 ? 1 : 2 ** (-10 * k) * Math.sin((k * 10 - 0.75) * (TAU / 3)) + 1);

/**
 * A keyframed value: `[time, value, ease?]` sorted by time, where `ease` shapes the segment that ends at that key
 * (default smooth). Before the first key it holds the first value, after the last the last.
 */
export type Key = readonly [number, number, Ease?];

export const keys = (t: number, ks: readonly Key[]): number => {
  const first = ks[0];
  if (!first) return 0;
  if (t <= first[0]) return first[1];
  for (let i = 1; i < ks.length; i++) {
    const b = ks[i] as Key;
    if (t <= b[0]) {
      const a = ks[i - 1] as Key;
      const span = b[0] - a[0];
      const k = span > 0 ? (t - a[0]) / span : 1;
      return lerp(a[1], b[1], (b[2] ?? smooth)(k));
    }
  }
  return (ks[ks.length - 1] as Key)[1];
};

/** Loop phase of `t` over a cycle of `dur` seconds, in [0, 1). */
export const phase = (t: number, dur: number): number => {
  const p = (t / dur) % 1;
  return p < 0 ? p + 1 : p;
};

/** sin over a loop phase, shifted by `off` (in cycles): use integer multiples of the clip's cycle to loop clean. */
export const wave = (p: number, off = 0, mul = 1): number => Math.sin((p * mul + off) * TAU);
export const cwave = (p: number, off = 0, mul = 1): number => Math.cos((p * mul + off) * TAU);

/** 0 → 1 over [a, b] with an ease. */
export const span = (t: number, a: number, b: number, e: Ease = smooth): number => e(clamp01((t - a) / (b - a)));

/** Up over [a, b], held, down over [c, d]. */
export const envelope = (t: number, a: number, b: number, c: number, d: number, e: Ease = smooth): number =>
  t < b ? span(t, a, b, e) : t < c ? 1 : 1 - span(t, c, d, e);

/** A damped spring that settles from 1 to 0 after an impulse at t=0 (follow-through, wobble). */
export const settle = (t: number, freq = 3, damp = 6): number => (t < 0 ? 0 : Math.exp(-damp * t) * Math.cos(TAU * freq * t));
